import {
  FETCH_WALLET_EMAILS_BEGIN,
  FETCH_WALLET_EMAILS_SUCCESS,
  FETCH_WALLET_EMAILS_FAILURE,
  UPDATE_USE_FOR_BEGIN,
  UPDATE_USE_FOR_SUCCESS,
  UPDATE_USE_FOR_FAILURE,
  REMOVE_EMAIL_BEGIN,
  REMOVE_EMAIL_SUCCESS,
  REMOVE_EMAIL_FAILURE,
  VALIDATE_EMAIL_BEGIN,
  VALIDATE_EMAIL_SUCCESS,
  VALIDATE_EMAIL_FAILURE,
  ADD_EMAIL_BEGIN,
  ADD_EMAIL_SUCCESS,
  ADD_EMAIL_FAILURE,
  VERIFY_OTP_BEGIN,
  VERIFY_OTP_SUCCESS,
  VERIFY_OTP_FAILURE,
  DISMISS_VERIFIED_NOTIFICATION,
  MARK_JUST_VERIFIED,
  RESEND_OTP_BEGIN,
  RESEND_OTP_SUCCESS,
  RESEND_OTP_FAILURE,
  CANCEL_OTP,
  RESEND_VERIFICATION_BEGIN,
  RESEND_VERIFICATION_SUCCESS,
  RESEND_VERIFICATION_FAILURE,
  SET_DEFAULT_EMAIL_BEGIN,
  SET_DEFAULT_EMAIL_SUCCESS,
  SET_DEFAULT_EMAIL_FAILURE,
  CLEAR_REMOVE_ERROR,
  CLEAR_CLASSIFICATION,
  CLEAR_ADD_ERROR,
  CLEAR_DEFAULT_EMAIL_ERROR,
  SET_INLINE_MODE,
  CLEAR_INLINE,
} from '../actions/walletEmails';

const initialInline = {
  mode: null, // 'add' | 'otp' | 'blocked' | 'suspicious' | 'locked' | 'collision' | null
  classification: null,
  domain: null,
  verificationId: null,
  targetEmailId: null, // email_id when OTP is for an existing row (resend verification)
  pendingEmail: null, // email being verified during the Add flow (added to list on VERIFY_OTP_SUCCESS)
  isValidating: false,
  isSubmitting: false,
  addError: null,
  otpError: null,
  attemptsRemaining: null,
  // 'set_default' when the user triggered the OTP flow via "Set as default"
  // on an unverified email — VERIFY_OTP_SUCCESS chains setDefaultEmail then.
  intent: null,
  mergeWidgetConfig: null,
};

export const initialState = {
  emails: [],
  error: null,
  isFetching: false,
  // fetchError is scoped to the emails list load. It's deliberately separate
  // from `error`, which UPDATE_USE_FOR_FAILURE also writes — keying the
  // list-level error state off a shared field would blank the table whenever a
  // use-for toggle failed. Stored as { error, errorCode }.
  fetchError: null,
  removeError: null,
  isRemoving: false,
  // defaultEmailError for set-default-email failures, separate from other errors.
  // Stored as { error, errorCode, afterVerification } so the banner can vary
  // copy based on whether the failure followed a successful OTP verification.
  defaultEmailError: null,
  // When non-null, the Email Verified notification is shown below the row
  // whose email_id matches. Set by VERIFY_OTP_SUCCESS (add or resend-verify
  // flow) and cleared by DISMISS_VERIFIED_NOTIFICATION or when another verify
  // flow starts.
  justVerifiedEmailId: null,
  inline: { ...initialInline },
};

const walletEmails = (state = initialState, action = {}) => {
  switch (action.type) {
    case FETCH_WALLET_EMAILS_BEGIN:
      return { ...state, error: null, fetchError: null, isFetching: true };
    case FETCH_WALLET_EMAILS_SUCCESS:
      // Clearing fetchError here as well as on BEGIN matters when two fetches
      // overlap (removeEmail refetches after handing off the default email): a
      // failure from the earlier request must not outlive a later success.
      return {
        ...state,
        emails: action.payload,
        fetchError: null,
        isFetching: false,
      };
    case FETCH_WALLET_EMAILS_FAILURE:
      return {
        ...state,
        error: action.payload.error,
        fetchError: {
          error: action.payload.error,
          errorCode: action.payload.errorCode,
        },
        isFetching: false,
      };

    case UPDATE_USE_FOR_BEGIN:
      return {
        ...state,
        emails: state.emails.map((email) =>
          email.email_id === action.payload.emailId
            ? {
                ...email,
                use_for: action.payload.useFor,
                _previousUseFor: email._previousUseFor ?? email.use_for,
              }
            : email,
        ),
      };
    case UPDATE_USE_FOR_SUCCESS:
      return {
        ...state,
        emails: state.emails.map((email) =>
          email.email_id === action.payload.emailId
            ? { ...email, _previousUseFor: undefined }
            : email,
        ),
      };
    case UPDATE_USE_FOR_FAILURE:
      return {
        ...state,
        emails: state.emails.map((email) =>
          email.email_id === action.payload.emailId && email._previousUseFor
            ? {
                ...email,
                use_for: email._previousUseFor,
                _previousUseFor: undefined,
              }
            : email,
        ),
        error: action.payload.error,
      };

    // Optimistic default email flip with clear-others semantics. The platform owns
    // uniqueness — only one email can be the default — so we mirror that locally:
    // set is_default: true on the target, false on all others. Stash a
    // `_previous_is_default` snapshot per affected row so FAILURE can revert.
    case SET_DEFAULT_EMAIL_BEGIN: {
      return {
        ...state,
        defaultEmailError: null,
        emails: state.emails.map((email) => ({
          ...email,
          _previous_is_default: email._previous_is_default ?? email.is_default,
          is_default: email.email_id === action.payload.emailId,
        })),
      };
    }
    case SET_DEFAULT_EMAIL_SUCCESS: {
      // Accept the server's authoritative email list (response.data). The
      // response objects don't carry _previous_is_default, so this also
      // implicitly clears the optimistic snapshots.
      return {
        ...state,
        emails: action.payload,
        defaultEmailError: null,
      };
    }
    case SET_DEFAULT_EMAIL_FAILURE: {
      return {
        ...state,
        emails: state.emails.map((email) =>
          email._previous_is_default !== undefined
            ? {
                ...email,
                is_default: email._previous_is_default,
                _previous_is_default: undefined,
              }
            : email,
        ),
        defaultEmailError: {
          error: action.payload.error,
          errorCode: action.payload.errorCode,
          afterVerification: !!action.payload.afterVerification,
        },
      };
    }

    case REMOVE_EMAIL_BEGIN:
      return { ...state, removeError: null, isRemoving: true };
    case REMOVE_EMAIL_SUCCESS:
      return {
        ...state,
        emails: state.emails.filter(
          (email) => email.email_id !== action.payload.emailId,
        ),
        removeError: null,
        isRemoving: false,
      };
    case REMOVE_EMAIL_FAILURE:
      return {
        ...state,
        removeError: {
          emailId: action.payload.emailId,
          errorCode: action.payload.errorCode,
          error: action.payload.error,
        },
        isRemoving: false,
      };

    case VALIDATE_EMAIL_BEGIN:
      return {
        ...state,
        inline: {
          ...state.inline,
          isValidating: true,
          classification: null,
          domain: null,
          addError: null,
        },
      };
    case VALIDATE_EMAIL_SUCCESS:
      return {
        ...state,
        inline: {
          ...state.inline,
          isValidating: false,
          classification: action.payload.classification,
          domain: action.payload.domain,
          addError: null,
        },
      };
    case VALIDATE_EMAIL_FAILURE:
      return {
        ...state,
        inline: {
          ...state.inline,
          isValidating: false,
          addError: action.payload,
        },
      };

    case ADD_EMAIL_BEGIN:
      return {
        ...state,
        justVerifiedEmailId: null,
        inline: { ...state.inline, isSubmitting: true, addError: null },
      };
    case ADD_EMAIL_SUCCESS: {
      // AXP-1613: addEmail creates a real entity server-side before OTP verification.
      // The POST response doesn't include email_id, so pendingEmail is a placeholder
      // (address/use_for/verification_status only). VERIFY_OTP_SUCCESS replaces it
      // with the server's authoritative created email (including email_id and
      // classification).
      return {
        ...state,
        inline: {
          ...state.inline,
          mode: 'otp',
          isSubmitting: false,
          verificationId: action.payload.verification_id,
          pendingEmail: {
            address: action.payload.address,
            use_for: action.payload.use_for,
            verification_status: 'pending',
            verified_at: null,
            // Newly added emails are never the default. Initialize as a
            // boolean (not undefined) so SET_DEFAULT_EMAIL_BEGIN's optimistic
            // snapshot (email._previous_is_default ?? email.is_default) is
            // always a defined value — selectDefaultEmailPending depends on
            // `!== undefined` to detect in-flight requests.
            is_default: false,
          },
        },
      };
    }
    case ADD_EMAIL_FAILURE: {
      const errorCode = action.payload.errorCode;
      let mode = state.inline.mode;
      const isDomainNotAllowed = errorCode === 'DOMAIN_NOT_ALLOWED';
      if (isDomainNotAllowed) {
        mode = action.payload.attemptsRemaining === 0 ? 'locked' : 'blocked';
      }
      return {
        ...state,
        inline: {
          ...state.inline,
          mode,
          isSubmitting: false,
          // DOMAIN_NOT_ALLOWED routes to BlockedDomainAlert (blocked) or
          // WalletLockedModal (locked); the inline addError WalletAlert is
          // not the canonical surface for those modes, so don't dirty the
          // state with an addError that nothing reads.
          addError: isDomainNotAllowed ? null : action.payload,
          attemptsRemaining: action.payload.attemptsRemaining,
        },
      };
    }

    case VERIFY_OTP_SUCCESS: {
      const verifiedAt = action.payload.verified_at || new Date().toISOString();
      const status =
        state.inline.classification === 'unknown'
          ? 'verified_pending_review'
          : 'verified';

      if (state.inline.targetEmailId) {
        // Resend verification — update existing email in the list; notify
        // below that row.
        return {
          ...state,
          emails: state.emails.map((e) =>
            e.email_id === state.inline.targetEmailId
              ? {
                  ...e,
                  verification_status: status,
                  verified_at: verifiedAt,
                }
              : e,
          ),
          justVerifiedEmailId: state.inline.targetEmailId,
          inline: { ...initialInline },
        };
      }

      // Add email flow — add the verified email to the list; notify below
      // the newly added row. Only ever uses the server's authoritative
      // created email (returned by the confirm endpoint's finalize! step),
      // which is the only object with a real email_id — the local
      // pendingEmail placeholder never has one (see addEmail). The
      // cross-device poll path doesn't return an email at all; rather than
      // fabricate a row from pendingEmail (which would have no email_id,
      // violating EmailTable's contract, and might not even correspond to a
      // real entity if this session turns out to be a collision), leave the
      // list untouched here and let that path's own fetchWalletEmails()
      // refetch bring in the authoritative row once it exists.
      // `status` (not the server's own verification_status, which only ever
      // returns 'verified'/'pending') is applied either way — it's the only
      // signal that distinguishes an unknown-classification email needing
      // manual review ('verified_pending_review') from a normal one.
      const verifiedEmail = action.payload.email
        ? { ...action.payload.email, verification_status: status }
        : null;
      return {
        ...state,
        emails: verifiedEmail ? [...state.emails, verifiedEmail] : state.emails,
        justVerifiedEmailId: verifiedEmail ? verifiedEmail.email_id : null,
        inline: { ...initialInline },
      };
    }

    case DISMISS_VERIFIED_NOTIFICATION:
      return { ...state, justVerifiedEmailId: null };

    case MARK_JUST_VERIFIED:
      return { ...state, justVerifiedEmailId: action.payload.emailId };

    case VERIFY_OTP_BEGIN:
      return {
        ...state,
        inline: {
          ...state.inline,
          otpError: null,
        },
      };

    case VERIFY_OTP_FAILURE: {
      // AXP-1613: a collision with another wallet's email is only revealed
      // now, after OTP possession is proven — route to the 'collision' mode
      // (its own inline panel) instead of the generic otpError display.
      // pendingEmail.address is kept (not cleared) so the panel can show
      // which address collided. Gated on pendingEmail being set (the add
      // flow only) since EmailCollisionAlert requires a real address —
      // ALREADY_EXISTS should never occur outside the add flow, but without
      // this guard a resend-verification session (no pendingEmail) hitting
      // it would render the panel with an undefined address.
      if (
        action.payload.errorCode === 'ALREADY_EXISTS' &&
        state.inline.pendingEmail
      ) {
        return {
          ...state,
          inline: {
            ...state.inline,
            mode: 'collision',
            // Stay null on the manifest-less path so absence is signaled the
            // same way as every other inline field, rather than an object of
            // undefineds.
            mergeWidgetConfig: action.payload.widgetManifest
              ? {
                  widgetManifest: action.payload.widgetManifest,
                  bffBaseUrl: action.payload.bffBaseUrl,
                  collisionEmail: action.payload.collisionEmail,
                }
              : null,
          },
        };
      }
      return {
        ...state,
        inline: {
          ...state.inline,
          otpError: action.payload,
        },
      };
    }

    case RESEND_OTP_BEGIN:
      return {
        ...state,
        inline: {
          ...state.inline,
          otpError: null,
        },
      };

    case RESEND_OTP_SUCCESS:
      // Clear any previous OTP error on successful resend
      return {
        ...state,
        inline: {
          ...state.inline,
          otpError: null,
        },
      };

    case RESEND_OTP_FAILURE:
      return {
        ...state,
        inline: {
          ...state.inline,
          otpError: action.payload,
        },
      };

    // AXP-1613: a real entity may already exist server-side at this point
    // (account-api's Wallet::Emails#start! calls EmailsAPI.create at Submit
    // time, before OTP, for the non-collision case) — but a collision
    // creates nothing, and this client has no way to tell which happened
    // (that's deliberate; see EmailCollisionAlert). pendingEmail never
    // carries an email_id either way, so optimistically adding it here would
    // violate EmailTable's required email_id and could show a row for an
    // email that was never actually created. Leave the list untouched;
    // cancelOtp (the thunk) refetches afterward so a real pending row shows
    // up if one exists, and nothing shows up if it doesn't.
    case CANCEL_OTP:
      return {
        ...state,
        inline: { ...initialInline },
      };

    case RESEND_VERIFICATION_BEGIN:
      return {
        ...state,
        justVerifiedEmailId: null,
        inline: {
          ...state.inline,
          otpError: null,
        },
      };

    case RESEND_VERIFICATION_SUCCESS:
      return {
        ...state,
        justVerifiedEmailId: null,
        inline: {
          ...initialInline,
          mode: 'otp',
          targetEmailId: action.payload.emailId,
          classification: action.payload.classification,
          verificationId: action.payload.verificationId,
          intent: action.payload.intent || null,
        },
      };

    case RESEND_VERIFICATION_FAILURE:
      // Reset inline to a clean state with just the error — don't carry over
      // mode/targetEmailId/verificationId from a prior session, which could
      // leave the UI pointing at a stale verificationId.
      return {
        ...state,
        inline: { ...initialInline, otpError: action.payload },
      };

    case CLEAR_CLASSIFICATION:
      return {
        ...state,
        inline: { ...state.inline, classification: null, domain: null },
      };

    case CLEAR_ADD_ERROR:
      return {
        ...state,
        inline: { ...state.inline, addError: null },
      };

    case SET_INLINE_MODE:
      return {
        ...state,
        inline: { ...state.inline, mode: action.payload },
      };
    case CLEAR_REMOVE_ERROR:
      return { ...state, removeError: null };

    case CLEAR_DEFAULT_EMAIL_ERROR:
      return { ...state, defaultEmailError: null };

    case CLEAR_INLINE:
      return { ...state, inline: { ...initialInline } };

    default:
      return state;
  }
};

// Find the email_id designated as the wallet's default email.
// Returns null if no email has is_default: true.
export const selectDefaultEmailId = (state) => {
  const match = (state.walletEmails.emails || []).find(
    (email) => email.is_default,
  );
  return match ? match.email_id : null;
};

// Check if a set-default-email request is in flight.
// Returns true if any email row has a _previous_is_default snapshot key set.
export const selectDefaultEmailPending = (state) => {
  return (state.walletEmails.emails || []).some(
    (email) => email._previous_is_default !== undefined,
  );
};

export default walletEmails;
